import { GradientOverflow } from 'components/GradientOverflow/GradientOverflow';
import React, { useEffect, useRef, useState } from 'react';
import type {
  DataElementReference,
  DataObject,
  ITextElement,
  Primitive,
  Token,
} from '@basis-theory/react-elements';
import { TextElement } from '@basis-theory/react-elements';

import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import type { OnboardingState } from './onboardingReducer';

const isDataElementReference = (
  value: unknown
): value is DataElementReference =>
  typeof value === 'object' &&
  value !== null &&
  'correlationId' in value &&
  'elementId' in value &&
  'path' in value;

const fontColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(255 255 255)' : 'rgb(13 13 13)';

const bgColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(38 38 38)' : 'rgb(230 230 230)';

const hoverBgColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(13 13 13)' : 'rgb(255 255 255)';

type BasisTheoryTextElementProps = {
  id: string;
  label: string;
  isDarkMode?: boolean;
  update: (id: string, ref: React.RefObject<ITextElement>) => void;
  token: Token | null;
};

export const LoadingTextElement = ({ delay }: { delay: number }) => (
  <div className="flex flex-col w-full">
    <span className="text-md text-main-500 ">Loading</span>
    <div
      className="h-f2 w-full bg-main-200 animate-pulse"
      style={{ animationDuration: '1s', animationDelay: `${delay}s` }}
    />
  </div>
);

export const FormLoading = () => (
  <div className="relative flex flex-col w-full gap-y-f2 h-fit">
    <LoadingTextElement delay={0.1} />
    <LoadingTextElement delay={0.2} />
    <LoadingTextElement delay={0.3} />
    <LoadingTextElement delay={0.4} />
    <LoadingTextElement delay={0.5} />
    <LoadingTextElement delay={0.6} />
    <GradientOverflow height="h-f15" />
  </div>
);

export const BasisTheoryTextElement = ({
  id,
  label,
  isDarkMode = false,
  update,
  token,
}: BasisTheoryTextElementProps) => {
  const ref = useRef<ITextElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isReady || !token || !ref.current) {
      return;
    }

    if (!token.data) {
      return;
    }

    const data = token.data as DataObject<Primitive>;
    const dataElement = data[id];
    if (isDataElementReference(dataElement)) {
      ref.current.setValue(dataElement);
    }
  }, [token, id, isReady]);

  return (
    <div className="flex flex-col w-full">
      <span className="text-md text-main-500 ">{label}</span>
      <TextElement
        id={id}
        ref={ref}
        onReady={() => {
          setIsReady(true);
        }}
        onBlur={() => update(id, ref)}
        style={{
          base: {
            fontFamily: 'FragmentMono, monospace',
            fontSize: '20px',
            lineHeight: '27px',
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
            padding: '0',
          },
          empty: {
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
          },
          complete: {
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
          },
        }}
      />
    </div>
  );
};

type OnboardingTextInputProps = {
  /** Key in onboarding reducer state, e.g. "businessName" */
  stateKey: string;
  label: string;
  state: OnboardingState;
  setField: (stateKey: string, value: string) => void;
};

export const OnboardingTextInput = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingTextInputProps) => {
  const value = state[stateKey as keyof OnboardingState] ?? '';
  if (typeof value === 'string') {
    return (
      <div className="flex flex-col w-full">
        <span className="text-md text-main-500 ">{label}</span>
        <TextInput
          padding="p-f0"
          height="h-f2"
          size="md"
          width="w-full"
          value={value}
          onChange={(e) => setField(stateKey, e.target.value)}
        />
      </div>
    );
  }
  return <span>Reduce State Not A String Field!</span>;
};

type OnboardingTextAreaProps = {
  stateKey: keyof OnboardingState;
  label: string;
  state: OnboardingState;
  setField: (stateKey: keyof OnboardingState, value: string) => void;
};

export const OnboardingTextArea = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingTextAreaProps) => {
  const value = state[stateKey as keyof OnboardingState] ?? '';
  if (typeof value === 'string') {
    return (
      <div className="flex flex-col">
        <span className="text-md text-main-500">{label}</span>
        <textarea
          value={value}
          onChange={(e) => setField(stateKey, e.target.value)}
          className="text-main text-md w-full min-h-f2 h-fit max-h-f12 p-f0 border-[0.5px] border-main-300 appearance-none select-none focus:outline-none focus:ring-0 focus:border-main-300"
        />
      </div>
    );
  }
  return <span>Reducer State Not a String!</span>;
};

type OnboardingDropdownProps = {
  stateKey: keyof OnboardingState;
  label: string;
  options: { value: string; label: string }[];
  state: OnboardingState;
  setField: (stateKey: keyof OnboardingState, value: string) => void;
};

export const OnboardingDropdown = ({
  stateKey,
  label,
  options,
  state,
  setField,
}: OnboardingDropdownProps) => {
  const value = state[stateKey as keyof OnboardingState] ?? '';
  if (typeof value === 'string') {
    return (
      <div className="flex flex-col ">
        <span className="text-md text-main-500">{label}</span>
        <Dropdown
          displayValue={value}
          options={options}
          onChange={(newValue) => setField(stateKey, newValue)}
          size="md"
          width="w-full"
          height="h-f2"
          padding="p-f0"
          backgroundColor="bg-main-200"
        />
      </div>
    );
  }
  return <span>Reducer State Not a String!</span>;
};

type OnboardingToggleProps = {
  stateKey: keyof OnboardingState;
  label: string;
  state: OnboardingState;
  setField: (stateKey: keyof OnboardingState, value: boolean) => void;
};

export const OnboardingToggle = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingToggleProps) => {
  const value = state[stateKey as keyof OnboardingState] ?? false;
  if (typeof value === 'boolean') {
    return (
      <div className="flex flex-col">
        <span className="text-md text-main min-w-f21">{label}</span>
        <Toggle
          controlled
          activeButton={value ? 'left' : 'right'}
          leftLabel="Yes"
          rightLabel="No"
          onLeftClick={() => setField(stateKey, true)}
          onRightClick={() => setField(stateKey, false)}
        />
      </div>
    );
  }
  return <span>{`Reducer state ${stateKey} is not a boolean`}</span>;
};

type OnboardingCheckBoxProps = {
  checked: boolean;
  label: React.ReactNode | string;
  onClick: () => void;
};

export const OnboardingCheckBox = ({
  checked,
  label,
  onClick,
}: OnboardingCheckBoxProps) => (
  <div className="flex flex-row items-start gap-x-[1ch]">
    <button
      type="button"
      onClick={onClick}
      className="aspect-square h-f2 text-main bg-transparent hover:bg-negative border-[0.5px] border-main-300"
    >
      {checked ? '✓' : ''}
    </button>
    {typeof label === 'string' ? (
      <span className="text-md text-main">{label}</span>
    ) : (
      label
    )}
  </div>
);

type OnboardingChecklistProps = {
  stateKey: keyof OnboardingState;
  selectedItems: string[];
  label: string;
  items: { value: string; label: string }[];
  setField: (stateKey: keyof OnboardingState, value: string[]) => void;
};

export const OnboardingChecklist = ({
  stateKey,
  selectedItems,
  label,
  items,
  setField,
}: OnboardingChecklistProps) => (
  <div className="flex flex-col ">
    <span className="text-md text-main-500 pb-f1">{label}</span>
    <div className="flex flex-col gap-y-f1 p-f1 border-main-300 border-[0.5px] max-h-f15 overflow-y-scroll">
      {items.map((item) => (
        <OnboardingCheckBox
          key={item.value}
          checked={selectedItems.includes(item.value)}
          label={item.label}
          onClick={() =>
            setField(
              stateKey,
              selectedItems.includes(item.value)
                ? selectedItems.filter((v) => v !== item.value)
                : [...selectedItems, item.value]
            )
          }
        />
      ))}
    </div>
  </div>
);
